import { FC, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { convertResToFormData } from "@/features/talent/utils/convertTalentFormData";
import { SharedTalentBasicInfo } from "@/features/sharing/components/SharedTalentBasicInfo";
import { TalentBasicInfo } from "@/features/talent/components/talent-detail/TalentBasicInfo";
import { TalentResidenceStatusInfo } from "@/features/talent/components/talent-detail/TalentResidenceStatusInfo";
import { SharedTalentLanguageAbility } from "@/features/sharing/components/SharedTalentLanguageAbility";
import { TalentEducationBackground } from "@/features/talent/components/talent-detail/TalentEducationBackground";
import { TalentWorkExperience } from "@/features/talent/components/talent-detail/TalentWorkExperience";
import { SharedTalentCertificateFiles } from "@/features/sharing/components/SharedTalentCertificateFiles";
import { SharedTalentProfileHeader } from "@/features/sharing/components/SharedTalentProfileHeader";
import { TGetTalentDto } from "@/api/services/main/talent/dtos/get-talent.dto";
import { getCredentialsKey } from "@/features/sharing/utils/session-credentials";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { SHARE_TYPE } from "@/api/services/main/sharing/dtos/sharing.dto";

type SharedTalentDetailProps = {
  hash: string;
  data: Record<string, unknown>;
  shareType?: number | null;
};

/** Convert full name to initials (e.g. "PUTU SUDI ARMADA" → "P.S.A") */
const toInitials = (name?: string | null): string => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts.map((p) => p.charAt(0).toUpperCase()).join(".");
};

/**
 * Mask PII fields for shared talent view (masked mode):
 * - 氏名 → initials (A.A)
 * - 呼び名 → hidden
 * - 生年月日 → hidden
 * - 電話番号 → hidden
 * - メールアドレス → hidden
 * - 現住所 → city level only (市以下削除)
 * - 本人顔写真 → default placeholder
 */
const maskTalentData = (data: Partial<TTalentForm>): Partial<TTalentForm> => ({
  ...data,
  fullName: toInitials(data.fullName),
  nickName: undefined,
  birthday: undefined,
  phoneNumber: undefined,
  email: undefined,
  address: undefined,
  zipCode: undefined,
  avatarFilePath: null,
});

export const SharedTalentDetail: FC<SharedTalentDetailProps> = ({ hash, data, shareType }) => {
  const talentFormData = useMemo(() => convertResToFormData(data as unknown as TGetTalentDto), [data]);

  const isMasked = shareType === SHARE_TYPE.MASKED;

  /** Retrieve shareUrl from sessionStorage for shared file access */
  const shareUrl = useMemo(() => {
    try {
      const stored = sessionStorage.getItem(getCredentialsKey(hash));
      if (stored) {
        const { shareUrl } = JSON.parse(stored) as { shareUrl: string };
        return shareUrl;
      }
    } catch {
      // ignore parse errors
    }
    return hash;
  }, [hash]);

  /** Apply PII masking only for masked share type */
  const displayData = useMemo(() => {
    if (!talentFormData) return null;
    return isMasked ? maskTalentData(talentFormData) : talentFormData;
  }, [talentFormData, isMasked]);

  if (!displayData) return null;

  return (
    <Stack direction="row" justifyContent="center">
      <Box width={764} mt={4} pb={8}>
        <SharedTalentProfileHeader defaultValues={displayData} isMasked={isMasked} shareUrl={shareUrl} />

        {isMasked ? (
          <SharedTalentBasicInfo defaultValues={displayData} />
        ) : (
          <TalentBasicInfo defaultValues={displayData} />
        )}
        <TalentResidenceStatusInfo defaultValues={displayData} isSharedView={isMasked} />
        <SharedTalentLanguageAbility defaultValues={displayData} />
        <TalentEducationBackground defaultValues={displayData} />
        <TalentWorkExperience defaultValues={displayData} />
        {!isMasked && <SharedTalentCertificateFiles defaultValues={displayData} shareUrl={shareUrl} />}
      </Box>
    </Stack>
  );
};
